import { useAtomValue } from 'jotai';

import { cn } from '@rocco/utils/cn';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import type { EnquirerType, EnquiryProjectKind } from 'generated/graphql';
import {
    ENQUIRER_TYPE_OPTIONS,
    FIELD_QUESTIONS,
    PROJECT_KIND_OPTIONS,
} from '~/modules/business-manager/forms/types';
import {
    GateHeading,
    GateSubcopy,
} from '~/modules-rocco/user/component/auth-modal/GateHeading';
import {
    GateFieldLabel,
    GateTextarea,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { GATE_CTA_CLASS } from '~/modules-rocco/user/component/auth-modal/gate-cta';
import { EnquiryGateCustomFields } from '~/modules-rocco/enquiry/component/EnquiryGateCustomFields';
import { EnquiryGateLocationField } from '~/modules-rocco/enquiry/component/EnquiryGateLocationField';
import { EnquiryGateDeclineNotice } from '~/modules-rocco/enquiry/component/EnquiryGateResults';
import { GateSelectField } from '~/modules-rocco/enquiry/component/GateSelectField';
import type { BookingFormAnswersModel } from '../hooks/use-booking-form-answers';
import {
    gateHeadingFor,
    type BookingLocationAnswer,
} from '../domain/book-meeting-flow';

interface BookMeetingGateDetailsProps {
    model: BookingFormAnswersModel;
    /** Named in the opening line, for a person's own form. */
    personName?: string;
    onContinue: () => void;
    /** Loading the form; the fields' shape is held so nothing jumps. */
    isLoading?: boolean;
    /** The words on the button that opened the modal, for its heading. */
    ctaLabel?: string;
}

/**
 * The booking form's questions, asked before a time is chosen: the answers
 * decide where the meeting routes, and so which calendar is offered next.
 *
 * Nothing is sent from here. The answers are held with the gate and go out
 * with the booking at the end, once the reader is signed in.
 */
export const BookMeetingGateDetails = ({
    model,
    personName,
    onContinue,
    isLoading = false,
    ctaLabel,
}: BookMeetingGateDetailsProps) => {
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);
    const { rules, answers, errors, branchPicker, predicted } = model;
    const declineMessage =
        predicted?.destination === 'Message' ? predicted.message : null;

    return (
        <div className="mx-auto flex w-full max-w-93.75 flex-1 flex-col">
            <GateHeading className="mt-3">
                {gateHeadingFor(null, ctaLabel)}
            </GateHeading>
            <GateSubcopy>
                {personName
                    ? `A few questions for ${personName} first, then pick a time.`
                    : 'A few questions first, then pick a time.'}
            </GateSubcopy>
            {isLoading || !rules ? (
                <div
                    className="mt-6 flex flex-col gap-3"
                    aria-busy="true"
                    aria-label="Loading questions"
                >
                    <div className="h-12 animate-pulse rounded-[0.75rem] bg-black/5" />
                    <div className="h-12 animate-pulse rounded-[0.75rem] bg-black/5" />
                    <div className="h-28 animate-pulse rounded-[0.75rem] bg-black/5" />
                </div>
            ) : (
                <form
                    className="mt-6 flex flex-1 flex-col gap-5.5"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (declineMessage !== null) return;
                        if (model.validate()) onContinue();
                    }}
                    noValidate
                >
                    {rules.collectLocation && (
                        <EnquiryGateLocationField
                            label={rules.locationLabel}
                            value={answers.location ?? undefined}
                            error={errors.location ?? undefined}
                            onSelect={(place) =>
                                model.setLocation(
                                    (place as
                                        | BookingLocationAnswer
                                        | undefined) ?? null,
                                )
                            }
                        />
                    )}

                    {branchPicker && (
                        <GateSelectField
                            label={branchPicker.label}
                            placeholder="Select a branch"
                            value={answers.branchId}
                            options={branchPicker.branchOptions.map(
                                (branch) => ({
                                    value: String(branch.value.branchId),
                                    label: branch.header,
                                }),
                            )}
                            error={errors.branch}
                            onChange={model.setBranchId}
                        />
                    )}

                    {rules.enquiryTypes.length > 0 && (
                        <GateSelectField
                            label={FIELD_QUESTIONS.enquiryType}
                            placeholder="Select an option"
                            value={answers.typeId}
                            options={rules.enquiryTypes.map((type) => ({
                                value: String(type.id),
                                label: type.title,
                            }))}
                            error={errors.enquiryType}
                            onChange={model.setEnquiryTypeId}
                        />
                    )}

                    {(rules.asksEnquirerType || rules.asksProjectKind) && (
                        <div
                            className={cn(
                                'grid gap-x-1.5 gap-y-5.5',
                                isDesktop &&
                                    rules.asksEnquirerType &&
                                    rules.asksProjectKind
                                    ? 'grid-cols-2'
                                    : 'grid-cols-1',
                            )}
                        >
                            {rules.asksEnquirerType && (
                                <GateSelectField
                                    label={FIELD_QUESTIONS.enquirerType}
                                    placeholder="Select an option"
                                    value={answers.enquirerType}
                                    options={ENQUIRER_TYPE_OPTIONS}
                                    error={errors.enquirerType}
                                    onChange={(value) =>
                                        model.setEnquirerType(
                                            value as EnquirerType,
                                        )
                                    }
                                />
                            )}
                            {rules.asksProjectKind && (
                                <GateSelectField
                                    label={FIELD_QUESTIONS.projectKind}
                                    placeholder="Select an option"
                                    value={answers.projectKind}
                                    options={PROJECT_KIND_OPTIONS}
                                    error={errors.projectKind}
                                    onChange={(value) =>
                                        model.setProjectKind(
                                            value as EnquiryProjectKind,
                                        )
                                    }
                                />
                            )}
                        </div>
                    )}

                    {rules.customFields.length > 0 && (
                        <EnquiryGateCustomFields
                            fields={rules.customFields}
                            answers={answers.fieldAnswers}
                            errors={errors.fields}
                            visitorCountry={model.visitorCountry}
                            twoColumns={isDesktop}
                            onChange={model.setFieldAnswer}
                        />
                    )}

                    {declineMessage !== null ? (
                        <EnquiryGateDeclineNotice message={declineMessage} />
                    ) : rules.messageEnabled ? (
                        <div className="flex flex-col gap-1.5">
                            <GateFieldLabel htmlFor="book-meeting-message">
                                Anything to add? (optional)
                            </GateFieldLabel>
                            <GateTextarea
                                id="book-meeting-message"
                                className="h-28"
                                placeholder={
                                    model.form?.messagePlaceholder ??
                                    'What would you like to talk about?'
                                }
                                value={answers.message}
                                onChange={(event) =>
                                    model.setMessage(event.target.value)
                                }
                            />
                        </div>
                    ) : null}

                    {declineMessage === null && (
                        <GateStickyFooter pinOn="both" className="mt-auto">
                            <button type="submit" className={GATE_CTA_CLASS}>
                                Continue
                            </button>
                        </GateStickyFooter>
                    )}
                </form>
            )}
        </div>
    );
};
